/**
 * Backup-clone drift flags (ADR 0030): pairs whose backup no longer matches
 * its primary's policy. Two signals feed this — the change feed (an admin
 * edited an owned field) and the daily sample sweep (the backstop for edits
 * the feed missed). Both raise the same review flag; the platform never
 * auto-writes, so each row offers a live re-diff and manual re-clone.
 */

import { useState } from "react";
import type { ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getDriftFlags, getPairCloneDrift, writePair } from "@pbxmgr/api";
import type { CloneDriftResult, DriftChange, DriftFlag } from "@pbxmgr/api";
import { Alert, Badge, Button, Spinner } from "@pbxmgr/ui";

import { usePermission } from "../auth/usePermission";
import { Panel } from "../dashboard/panels";

function changeLine(c: DriftChange): string {
  return `${c.field}: "${c.old ?? ""}" → "${c.new ?? ""}"${c.who ? ` (${c.who})` : ""}`;
}

function FlagRow({ flag, canManage }: { flag: DriftFlag; canManage: boolean }): ReactNode {
  const qc = useQueryClient();
  const [diff, setDiff] = useState<CloneDriftResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const check = useMutation({
    mutationFn: () => getPairCloneDrift(flag.pair_id),
    onSuccess: (r) => {
      setError(null);
      setDiff(r);
    },
    onError: () => setError("Could not read the pair live."),
  });
  const reclone = useMutation({
    mutationFn: () => writePair(flag.pair_id),
    onSuccess: () => {
      setError(null);
      void qc.invalidateQueries({ queryKey: ["topo"] });
    },
    onError: () => setError("Re-clone failed — see node status / audit log."),
  });

  const changes = [...flag.primary_changes, ...flag.backup_changes];

  return (
    <tr>
      <td>
        {flag.primary} <span className="ui-empty">↔</span> {flag.backup}
      </td>
      <td>
        {flag.sources.map((s) => (
          <Badge key={s} tone={s === "sample" ? "degraded" : "unknown"}>
            {s === "sample" ? "sample sweep" : "change feed"}
          </Badge>
        ))}
      </td>
      <td>
        <ul className="drift-changes">
          {changes.map((c, i) => (
            <li key={i}>{changeLine(c)}</li>
          ))}
          {flag.sampled_fields.length > 0 ? (
            <li>sample: {flag.sampled_fields.join(", ")}</li>
          ) : null}
        </ul>
        {diff ? (
          <div className="drift-diff">
            {diff.status === "in_sync" ? (
              <Badge tone="online">live: in sync now</Badge>
            ) : diff.status === "unknown" ? (
              <Badge tone="degraded">live: could not read</Badge>
            ) : (
              <>
                <Badge tone="degraded">live drift</Badge>{" "}
                {Object.entries(diff.drift)
                  .map(
                    ([f, v]) =>
                      `${f}: expected "${String(v.expected)}", got "${String(v.actual)}"`,
                  )
                  .join("; ")}
              </>
            )}
          </div>
        ) : null}
        {error ? <Alert>{error}</Alert> : null}
      </td>
      <td>
        <div className="pairing-actions">
          <Button variant="secondary" disabled={check.isPending} onClick={() => check.mutate()}>
            {check.isPending ? "Checking…" : "Check live"}
          </Button>
          {canManage ? (
            <Button disabled={reclone.isPending} onClick={() => reclone.mutate()}>
              {reclone.isPending ? "Re-cloning…" : "Re-clone"}
            </Button>
          ) : null}
        </div>
      </td>
    </tr>
  );
}

export function DriftFlagsPanel(): ReactNode {
  const canManage = usePermission("extensions.manage");
  const flags = useQuery({ queryKey: ["topo", "drift-flags"], queryFn: getDriftFlags });
  const items = flags.data?.items ?? [];

  return (
    <Panel title={`Backup drift flags (${items.length})`}>
      <p>
        Pairs whose backup no longer matches its primary — an admin edited an owned field
        (change feed), or the daily sample sweep found a divergence the feed missed. Review
        each: “Check live” re-diffs now, “Re-clone” re-applies the primary’s settings.
      </p>
      {flags.isLoading ? (
        <Spinner />
      ) : items.length === 0 ? (
        <div className="ui-empty">No drift flagged. Backups match their primaries.</div>
      ) : (
        <table className="ui-table">
          <thead>
            <tr>
              <th>Pair</th>
              <th>Found by</th>
              <th>What changed</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {items.map((f) => (
              <FlagRow key={f.pair_id} flag={f} canManage={canManage} />
            ))}
          </tbody>
        </table>
      )}
    </Panel>
  );
}
